import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo, useState } from 'react';

import {
  useApiKeys,
  useApiKeyMaxExpiryDays,
  useCreateApiKey,
  useDeleteApiKey
} from '@rhino-project/core/queries';
import {
  AlertDialog,
  Button,
  Card,
  CardContent,
  Chip,
  ListBox,
  ListBoxItem,
  Select,
  SelectIndicator,
  SelectPopover,
  SelectTrigger,
  SelectValue,
  Switch,
  SwitchControl,
  SwitchThumb,
  SwitchContent,
  TextField,
  Input,
  Label,
  Typography
} from '@heroui/react';

interface ApiKeyRow {
  id: number;
  name: string;
  token_prefix: string;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

const isReadOnly = (scopes: string[]) => !scopes.includes('full-api:write');

interface ExpiryOption {
  value: string;
  label: string;
  days: number | null;
}

const EXPIRY_CANDIDATES: ExpiryOption[] = [
  { value: '30', label: '30 days', days: 30 },
  { value: '90', label: '90 days', days: 90 },
  { value: '180', label: '180 days', days: 180 },
  { value: '365', label: '1 year', days: 365 }
];

// Build the expiry choices for the create form. With a ceiling (maxDays), offer
// only the candidates within it, plus the ceiling itself when it isn't already
// one. With no ceiling, offer all candidates plus a "No expiry" choice.
export const expiryOptions = (maxDays: number | null): ExpiryOption[] => {
  if (maxDays == null) {
    return [
      ...EXPIRY_CANDIDATES,
      { value: 'none', label: 'No expiry', days: null }
    ];
  }

  const within = EXPIRY_CANDIDATES.filter(
    (option) => option.days != null && option.days <= maxDays
  );

  if (!within.some((option) => option.days === maxDays)) {
    within.push({
      value: String(maxDays),
      label: `${maxDays} days`,
      days: maxDays
    });
  }

  return within;
};

// Convert a selected option's day count into an absolute ISO expiry (or null
// for "No expiry"), measured from now.
const expiresAtFor = (days: number | null): string | null =>
  days == null ? null : new Date(Date.now() + days * 86_400_000).toISOString();

export const ApiKeysSettings = () => {
  const { t, locale } = useTranslation();
  const { data } = useApiKeys();
  const { mutate: create, isPending: creating } = useCreateApiKey();
  const { mutate: revoke, isPending: revoking } = useDeleteApiKey();

  const maxDays = useApiKeyMaxExpiryDays();
  const options = useMemo(() => expiryOptions(maxDays), [maxDays]);
  const defaultExpiry = useMemo(
    () =>
      options.find((option) => option.value === '90')?.value ??
      options[options.length - 1].value,
    [options]
  );

  const [name, setName] = useState('');
  const [fullAccess, setFullAccess] = useState(false);
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const keys = (data ?? []) as ApiKeyRow[];

  const handleCreate = () => {
    if (!name.trim()) return;
    const days =
      options.find((option) => option.value === expiry)?.days ?? null;
    create(
      {
        name: name.trim(),
        full_access: fullAccess,
        expires_at: expiresAtFor(days)
      },
      {
        onSuccess: (created: unknown) => {
          const c = created as { token: string };
          setRevealedToken(c.token);
          setName('');
          setFullAccess(false);
          setExpiry(defaultExpiry);
        }
      }
    );
  };

  const handleCopyToken = () => {
    if (revealedToken && navigator.clipboard) {
      navigator.clipboard.writeText(revealedToken);
      setCopied(true);
    }
  };

  const handleDone = () => {
    setRevealedToken(null);
    setCopied(false);
  };

  return (
    <section className="flex flex-col gap-2">
      <Typography.Heading level={4} weight="medium">
        {t('rhino.frontend.messages.api_keys')}
      </Typography.Heading>
      <p className="text-sm text-muted">
        {t(
          'rhino.frontend.messages.api_keys_let_you_authenticate_api_requests_include_the_key_in_the'
        )}{' '}
        <code>
          {t('rhino.frontend.messages.authorization_bearer_lt_key_gt')}
        </code>{' '}
        {t('rhino.frontend.messages.header_of_each_request')}
      </p>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-row items-end gap-4">
            <div className="flex-1">
              <TextField value={name} onChange={setName}>
                <Label>{t('rhino.frontend.messages.key_name')}</Label>
                <Input
                  placeholder={t('rhino.frontend.messages.e_g_my_integration')}
                />
              </TextField>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                {t('rhino.frontend.messages.expires')}
              </span>
              <Select
                aria-label={t('rhino.frontend.messages.expires')}
                value={expiry}
                onChange={(key) => setExpiry(String(key))}
                className="w-40"
              >
                <SelectTrigger>
                  <SelectValue />
                  <SelectIndicator />
                </SelectTrigger>
                <SelectPopover>
                  <ListBox>
                    {options.map((option) => (
                      <ListBoxItem
                        key={option.value}
                        id={option.value}
                        textValue={
                          option.days == null
                            ? t('rhino.frontend.messages.no_expiry')
                            : t('rhino.frontend.messages.days', {
                                count: option.days
                              })
                        }
                      >
                        {option.days == null
                          ? t('rhino.frontend.messages.no_expiry')
                          : t('rhino.frontend.messages.days', {
                              count: option.days
                            })}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </SelectPopover>
              </Select>
            </div>
            <Switch
              isSelected={fullAccess}
              onChange={setFullAccess}
              className="pb-2"
            >
              <SwitchContent>
                <SwitchControl>
                  <SwitchThumb />
                </SwitchControl>
                {t('rhino.frontend.messages.full_access')}
              </SwitchContent>
            </Switch>
          </div>

          <div>
            <Button
              isDisabled={creating || !name.trim()}
              onPress={handleCreate}
            >
              {creating
                ? t('rhino.frontend.messages.creating')
                : t('rhino.frontend.messages.create_key')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {keys.length === 0 && (
        <span className="text-sm text-muted">
          {t('rhino.frontend.messages.no_api_keys_yet')}
        </span>
      )}

      {keys.map((key) => (
        <Card key={key.id}>
          <CardContent className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{key.name}</span>
                <Chip size="sm" variant="soft">
                  {isReadOnly(key.scopes)
                    ? t('rhino.frontend.messages.read_only')
                    : t('rhino.frontend.messages.full_access_badge')}
                </Chip>
              </div>
              <span className="text-sm text-muted">
                {key.token_prefix}…
                {key.last_used_at
                  ? ' · ' +
                    t('rhino.frontend.messages.last_used_value', {
                      value1: new Date(key.last_used_at).toLocaleDateString(
                        locale.replaceAll('_', '-')
                      )
                    })
                  : ' · ' + t('rhino.frontend.messages.never_used')}
                {key.expires_at
                  ? ' · ' +
                    t('rhino.frontend.messages.expires_on', {
                      date: new Date(key.expires_at).toLocaleDateString(
                        locale.replaceAll('_', '-')
                      )
                    })
                  : ''}
              </span>
            </div>
            <Button
              size="sm"
              variant="danger-soft"
              isDisabled={revoking}
              onPress={() => revoke(key.id)}
            >
              {t('rhino.frontend.messages.revoke')}
            </Button>
          </CardContent>
        </Card>
      ))}

      <AlertDialog isOpen={revealedToken !== null} onOpenChange={handleDone}>
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>
                  {t('rhino.frontend.messages.your_new_api_key')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-warning font-medium">
                    {t('rhino.frontend.messages.api_key_copy_notice')}
                  </p>
                  <code className="block rounded bg-surface p-2 text-sm break-all select-all">
                    {revealedToken}
                  </code>
                </div>
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={handleCopyToken}>
                  {copied
                    ? t('rhino.frontend.messages.copied')
                    : t('rhino.frontend.messages.copy_key')}
                </Button>
                <Button onPress={handleDone}>
                  {t('rhino.frontend.messages.done')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </section>
  );
};
